import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, ShieldCheck } from "lucide-react";

export const POLICIES = [
  {
    slug: "code-of-conduct",
    title: "Code of Conduct",
    intent: "How we expect every person associated with Alpha Community Care — workers, leadership, contractors — to behave.",
    body: [
      ["Respectful practice", "We treat participants, families, colleagues and community members with respect, courtesy and cultural safety at all times."],
      ["Honesty and integrity", "We act honestly, transparently and in the best interests of the people we support. We do not misrepresent services, qualifications or outcomes."],
      ["Person-centred support", "We design and deliver supports around individual goals, preferences and choices."],
      ["Safety and safeguarding", "We actively promote physical, emotional and psychological safety. We identify and act on risk."],
      ["Confidentiality", "We protect personal information and only share it where there is a clear operational, clinical or legal basis."],
      ["No tolerance for abuse", "Any form of abuse, neglect, violence, exploitation or discrimination is grounds for immediate action."],
    ],
  },
  {
    slug: "complaints-feedback",
    title: "Complaints & Feedback",
    intent: "How participants, families and stakeholders can raise concerns, compliments or ideas — and how we respond.",
    body: [
      ["Your right to speak up", "Every participant and family member has the right to raise concerns without fear of negative consequences."],
      ["How to submit feedback", "Talk to any Alpha worker, email hello@alphacommunitycare.com.au, call 1300 380 692, or use our Complaints form."],
      ["Our response", "We acknowledge complaints within 2 business days, investigate promptly, and provide a clear written response."],
      ["External escalation", "You may contact the NDIS Quality and Safeguards Commission at any time (1800 035 544 or ndiscommission.gov.au)."],
      ["Continuous improvement", "Feedback is reviewed monthly and used to improve services, training and policies."],
    ],
  },
  {
    slug: "decision-making",
    title: "Decision Making",
    intent: "How we support participants to make their own decisions — and how we act when they need additional support.",
    body: [
      ["Presumption of capacity", "We presume every participant has decision-making capacity unless there is a documented reason to do otherwise."],
      ["Supported decision making", "We provide accessible information, time and support so participants can make their own decisions wherever possible."],
      ["Substitute decision making", "Where formal arrangements exist (e.g. guardianship, nominee, enduring power of attorney), we respect and follow them."],
      ["Dignity of risk", "We acknowledge that everyday decisions carry everyday risks. We support choice and manage risk transparently."],
      ["Documentation", "Significant decisions, consents and substitute arrangements are documented in the participant's file."],
    ],
  },
  {
    slug: "privacy-dignity",
    title: "Privacy & Dignity",
    intent: "How we protect participants' privacy, personal information and sense of self across everything we do.",
    body: [
      ["Your personal information", "We only collect what we need, store it securely, and only share it with explicit consent or legal obligation."],
      ["In your home", "We respect your home as your home. We knock, we ask, we clean up after ourselves."],
      ["Personal care", "Personal care is delivered privately, respectfully and in line with your preferences."],
      ["Photography and media", "We never photograph or record you without explicit, documented consent."],
      ["Access and correction", "You can ask to see your information and correct anything you believe is wrong."],
    ],
  },
  {
    slug: "zero-tolerance",
    title: "Zero Tolerance",
    intent: "Alpha Community Care has zero tolerance for abuse, neglect, violence, exploitation or discrimination — by anyone, against anyone.",
    body: [
      ["Scope", "This applies to workers, contractors, families, visitors and participants. It covers physical, sexual, financial, emotional and psychological abuse and neglect."],
      ["Prevention", "We screen, train, supervise and audit. We actively identify and reduce risks of harm."],
      ["Reporting", "All concerns of abuse, neglect or exploitation are reported immediately via internal escalation and, where required, to police, NDIS Commission and other authorities."],
      ["Response", "We respond with urgency, transparency and compassion. Participants are protected, supported and believed."],
      ["Accountability", "Substantiated breaches result in disciplinary action up to termination of employment or engagement, and referral to authorities."],
    ],
  },
];

export function PoliciesHubPage() {
  return (
    <div data-testid="page-policies">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Policies</div>
          <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">How we run — written down.</h1>
          <p className="mt-5 max-w-2xl text-lg text-[color:var(--alpha-mute)]">Trust requires transparency. Our core policies are published here — aligned to the NDIS Practice Standards and regularly reviewed.</p>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {POLICIES.map((p) => (
            <Link key={p.slug} to={`/policies/${p.slug}`} className="alpha-card p-7 group" data-testid={`policy-${p.slug}`}>
              <FileText className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
              <h3 className="mt-4 font-display text-xl font-semibold group-hover:text-[color:var(--alpha-purple-700)] transition-colors">{p.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--alpha-mute)]">{p.intent}</p>
              <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)]">Read policy →</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PolicyDetailPage() {
  const { slug } = useParams();
  const p = POLICIES.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/policies" replace />;
  return (
    <div data-testid={`page-policy-${p.slug}`}>
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-10 pb-10">
          <Link to="/policies" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-4 h-4" /> Policies
          </Link>
          <div className="alpha-overline mt-4">Policy</div>
          <h1 className="mt-3 text-3xl sm:text-[44px] font-display font-medium tracking-tight">{p.title}</h1>
          <p className="mt-3 max-w-2xl text-[color:var(--alpha-mute)]">{p.intent}</p>
        </div>
      </section>
      <section className="alpha-container py-14 max-w-3xl space-y-8">
        {p.body.map(([t, d]) => (
          <div key={t}>
            <h2 className="font-display text-xl font-semibold">{t}</h2>
            <p className="mt-2 text-[color:var(--alpha-mute)] leading-relaxed">{d}</p>
          </div>
        ))}
        <div className="alpha-card p-6 flex items-start gap-4 bg-[color:var(--alpha-purple-50)] border-[color:var(--alpha-line-strong)]">
          <ShieldCheck className="w-6 h-6 text-[color:var(--alpha-purple-700)] shrink-0" />
          <div className="text-sm text-[color:var(--alpha-ink)]">
            Questions or concerns about this policy? Contact us on <a href="tel:1300380692" className="font-semibold text-[color:var(--alpha-purple-700)]">1300 380 692</a> or <a href="mailto:hello@alphacommunitycare.com.au" className="font-semibold text-[color:var(--alpha-purple-700)]">hello@alphacommunitycare.com.au</a>. You can also contact the NDIS Commission on 1800 035 544.
          </div>
        </div>
        <p className="text-xs text-[color:var(--alpha-mute)] pt-6 border-t border-[color:var(--alpha-line)]">Last reviewed: {new Date().toISOString().slice(0, 10)}</p>
      </section>
    </div>
  );
}
